/**
 * Pure matching logic for the deeplink-highlight system. No React, DOM, or
 * router imports here so the rules stay trivially unit-testable.
 *
 * A settings page is deeplinked with `?group=<groupId>&setting=<settingId>`, and
 * an optional `?highlight=<mode>` controls what lights up.
 */

/**
 * `?highlight` mode:
 * - `one` (default): highlight the most specific target only — the setting when
 *   a setting is given, otherwise the group.
 * - `both`: highlight the group AND the setting when both are given.
 * - `debug_groups`: highlight every group (ignores params).
 * - `debug_settings`: highlight every setting (ignores params).
 * - `debug_both`: highlight every group and every setting (ignores params).
 */
export type HighlightMode =
  | "one"
  | "both"
  | "debug_groups"
  | "debug_settings"
  | "debug_both";

export interface HighlightParams {
  group: string | null;
  setting: string | null;
  mode: HighlightMode;
}

/**
 * What a given wrapper is. A `setting` carries the id of the group it is nested
 * in (via context), or `undefined` when it is standalone (not inside a group).
 */
export type HighlightTarget =
  | { kind: "group"; id: string }
  | { kind: "setting"; id: string; groupId: string | undefined };

/** Parse the raw `?highlight` value into a mode, defaulting to `one`. */
export function parseHighlightMode(raw: string | null): HighlightMode {
  switch (raw) {
    case "both":
    case "debug_groups":
    case "debug_settings":
    case "debug_both":
      return raw;
    default:
      return "one";
  }
}

/** Whether this target should be highlighted (should glow) for these params. */
export function isHighlightActive(
  target: HighlightTarget,
  params: HighlightParams,
): boolean {
  switch (params.mode) {
    case "debug_groups":
      return target.kind === "group";
    case "debug_settings":
      return target.kind === "setting";
    case "debug_both":
      return true;
    case "one":
    case "both":
      break;
  }

  if (target.kind === "group") {
    if (params.group === null || params.group !== target.id) {
      return false;
    }
    // In `one` mode the group only highlights when no specific setting is
    // targeted; in `both` mode it highlights alongside the setting.
    return params.mode === "both" || params.setting === null;
  }

  if (target.groupId === undefined) {
    return params.setting !== null && params.setting === target.id;
  }

  return (
    params.setting !== null &&
    params.setting === target.id &&
    params.group !== null &&
    params.group === target.groupId
  );
}

/**
 * Whether an active GROUP should render in the subdued (grey) style because a
 * specific setting is highlighted at the same time — so the orange setting
 * inside it stays the focus.
 */
export function isGroupSubdued(params: HighlightParams): boolean {
  if (params.mode === "debug_both") {
    return true;
  }
  if (params.mode === "both") {
    return params.setting !== null;
  }
  return false;
}

/**
 * Among active targets, whether THIS one should own the scroll ("most specific
 * wins"). Debug modes never auto-scroll — they highlight everything at once.
 */
export function shouldOwnScroll(
  target: HighlightTarget,
  params: HighlightParams,
): boolean {
  if (
    params.mode === "debug_groups" ||
    params.mode === "debug_settings" ||
    params.mode === "debug_both"
  ) {
    return false;
  }
  if (!isHighlightActive(target, params)) {
    return false;
  }
  if (target.kind === "group") {
    return params.setting === null;
  }
  return true;
}
